import { defineStore } from 'pinia';
import { computed, shallowRef } from 'vue';
import { BrowserWebcamStreamingConfig } from '../Interfaces/BrowserWebcamStreaming';
import { MediaMTXWebRTCPublisher } from '../utils/MediaMTXWebRTCPublisher';
import {
	createStreamingTelemetrySession,
	generateSessionId,
	StreamingTelemetrySession,
} from '../utils/StreamingTelemetry';

type StreamingStatus =
	| 'idle'
	| 'starting'
	| 'publishing'
	| 'retrying'
	| 'failed'
	| 'stopped';

interface MockWebcamWindow extends Window {
	mock_webcam?: boolean;
	mock_webcam_base64?: string;
}

const mockWebcamState = (() => {
	if (typeof window === 'undefined') {
		return {
			enabled: false,
			base64: '',
		};
	}

	const globalWindow = window as MockWebcamWindow;

	return {
		enabled: Boolean(globalWindow.mock_webcam),
		base64: globalWindow.mock_webcam_base64 ?? '',
	};
})();

export const browserWebcamStreamingStore = defineStore(
	'browserWebcamStreaming',
	() => {
		const config = shallowRef<BrowserWebcamStreamingConfig | null>(null);
		const stream = shallowRef<MediaStream | null>(null);
		const publisher = shallowRef<MediaMTXWebRTCPublisher | null>(null);
		const telemetrySession = shallowRef<StreamingTelemetrySession | null>(
			null
		);
		const status = shallowRef<StreamingStatus>('idle');
		const lastError = shallowRef<string | null>(null);

		const isEnabled = computed(() => config.value !== null);
		const isPublishing = computed(() => status.value === 'publishing');
		const isMock = computed(() => mockWebcamState.enabled);
		const mockPhotoBase64 = computed(() => mockWebcamState.base64);

		async function ensureStarted(
			nextConfig: BrowserWebcamStreamingConfig | null | undefined
		) {
			if (!nextConfig) {
				return;
			}

			if (
				publisher.value !== null &&
				stream.value !== null &&
				config.value?.whip_url === nextConfig.whip_url
			) {
				return;
			}

			stop('restart');

			config.value = nextConfig;
			status.value = 'starting';
			lastError.value = null;

			if (mockWebcamState.enabled) {
				stream.value = null;
				status.value = 'publishing';
				return;
			}

			try {
				stream.value = await navigator.mediaDevices.getUserMedia({
					video: {
						width: { ideal: nextConfig.video.width },
						height: { ideal: nextConfig.video.height },
						frameRate: { ideal: nextConfig.video.frame_rate },
					},
					audio: nextConfig.audio,
				});

				// One telemetry session per publisher instance. The Pinia
				// publisher singleton survives Inertia navigations, so this
				// session_id stays stable for the lifetime of the WHIP
				// publisher (and is reset together with it on a fresh start).
				const telemetry = createStreamingTelemetrySession({
					sessionId: generateSessionId(),
					enrollmentTestId: nextConfig.enrollment_test_id,
					actor: 'publisher',
				});
				telemetrySession.value = telemetry;

				const r = nextConfig.reconnect;
				publisher.value = new MediaMTXWebRTCPublisher({
					url: nextConfig.whip_url,
					stream: stream.value,
					videoCodec: nextConfig.video_codec,
					videoBitrate: nextConfig.video_bitrate,
					audioCodec: nextConfig.audio_codec,
					audioBitrate: nextConfig.audio_bitrate,
					audioVoice: nextConfig.audio_voice,
					reconnect: r
						? {
								disconnectedHysteresisMs:
									r.disconnected_hysteresis_ms,
								deleteTimeoutMs: r.delete_timeout_ms,
								backoffMs: r.backoff_ms,
								catastrophicFailuresThreshold:
									r.catastrophic_failures_threshold,
								catastrophicWindowMs: r.catastrophic_window_ms,
								connectedResetThresholdMs:
									r.connected_reset_threshold_ms,
						  }
						: undefined,
					telemetry: (event, details) =>
						telemetry.track(event, details),
					onConnected: () => {
						status.value = 'publishing';
						lastError.value = null;
					},
					onError: (error) => {
						// The publisher prefixes the catastrophic message with
						// "catastrophic:" — surface that as a terminal `failed`
						// status so the UI can stop showing "Reconectando..." and
						// tell the user to contact their CFC. All other errors are
						// mid-recovery and remain `retrying`.
						if (error.startsWith('catastrophic:')) {
							status.value = 'failed';
						} else {
							status.value = 'retrying';
						}
						lastError.value = error;
					},
				});
			} catch (error) {
				status.value = 'failed';
				lastError.value =
					error instanceof Error ? error.message : String(error);
				throw error;
			}
		}

		function attachPreview(video: HTMLVideoElement | null) {
			if (video === null || stream.value === null) {
				return;
			}

			if (video.srcObject !== stream.value) {
				video.srcObject = stream.value;
			}

			video.muted = true;
			video.playsInline = true;
			video.play().catch(() => {});
		}

		async function takePhoto(): Promise<string | null> {
			if (mockWebcamState.enabled) {
				return mockWebcamState.base64 || null;
			}

			const videoTrack = stream.value?.getVideoTracks()[0];
			if (!stream.value || !videoTrack) {
				return null;
			}

			const video = document.createElement('video');
			video.srcObject = stream.value;
			video.muted = true;
			video.playsInline = true;

			const settings = videoTrack.getSettings();
			const canvas = document.createElement('canvas');
			canvas.width = settings.width ?? 640;
			canvas.height = settings.height ?? 480;

			const context = canvas.getContext('2d');
			if (context === null) {
				return null;
			}

			return new Promise<string | null>((resolve) => {
				video.onloadedmetadata = () => {
					video
						.play()
						.then(() => {
							canvas.width = video.videoWidth || canvas.width;
							canvas.height = video.videoHeight || canvas.height;
							context.drawImage(
								video,
								0,
								0,
								canvas.width,
								canvas.height
							);
							resolve(
								canvas.toDataURL('image/jpeg').split(',')[1]
							);
						})
						.catch(() => resolve(null));
				};
			});
		}

		function stop(reason = 'stopped') {
			// Emit a final close event before tearing down the publisher
			// (the publisher itself emits publisher.close from close(), but
			// the reason — passed by the caller — is the bit we want).
			telemetrySession.value?.track('publisher.stop', { reason });

			publisher.value?.close();
			publisher.value = null;
			telemetrySession.value = null;

			stream.value?.getTracks().forEach((track) => track.stop());
			stream.value = null;

			status.value = reason === 'restart' ? 'idle' : 'stopped';
		}

		return {
			config,
			stream,
			status,
			lastError,
			isEnabled,
			isPublishing,
			isMock,
			mockPhotoBase64,
			ensureStarted,
			attachPreview,
			takePhoto,
			stop,
		};
	}
);
